// M7.3 — shared types for the agent-shell layer.
//
// Wire contract with backend #743 (M7.2), amendment #130:
//   POST /api/agent/surface-context
//   body:     { surface, brandId, planId?, focusId? }
//   response: { summary, nudges[], starters[] }
//
// #743 is NO-GO and being fixed. Extra fields below are optional and
// a missing array must not throw. Re-check this contract after #743's
// fix lands — do not treat the optional fields as frozen.
//
// The client never sends a narration arm. When the server cannot
// narrate, callers show the rules text (`rulesText`, else `summary`).
//
// AGENT_SHELL_VISUAL_STATUS marks every visual in this layer as
// pending Mark's design sign-off. These components are not the
// final panel or M8 treatment.

export const AGENT_SHELL_VISUAL_STATUS = 'pending-mark-sign-off' as const;

export type NudgeConfirmFlow =
  | { kind: 'open-dialog'; target: string; prefill?: Record<string, unknown> }
  | { kind: 'navigate';    target: string; prefill?: Record<string, unknown> };

export type Nudge = {
  id:          string;
  kind:        string;
  title:       string;
  body:        string;
  confirmFlow?: NudgeConfirmFlow;
};

export type Starter = {
  id:   string;
  text: string;
};

export type AttentionTarget = {
  entityType: string;
  entityId:   string;
  reason?:    string;
};

export type NarrationStatus = 'ok' | 'unavailable' | 'failed' | 'rules';

export type SurfaceContextResponse = {
  summary:            string;
  /** Rules prose. Preferred over `summary` when narration did not run. */
  rulesText?:         string;
  nudges?:            Nudge[];
  starters?:          Starter[];
  attentionTargets?:  AttentionTarget[];
  cacheKey?:          string;
  lastGeneratedAt?:   string;
  /** Echo of the brand the server built this payload for, when #743 sends it. */
  brandId?:           string;
  narrationArm?:      'rules' | 'llm';
  narration?:         string;
  narrationStatus?:   NarrationStatus;
};

// Input the surface passes to the hook. `surface` is required — the
// backend keys cache + rate limit on it. planId/focusId are optional
// qualifiers for a nested surface (e.g. a plan's performance tab).
export type SurfaceContextInput = {
  surface: string;
  planId?: string;
  focusId?: string;
};

/**
 * Text the operator should read. LLM prose only when the server says
 * narration succeeded. A failure, an "unavailable" status, or a missing
 * narration string keeps the rules text — the summary is not dropped.
 */
export function surfaceContextSummary(data: SurfaceContextResponse): string {
  const rules = typeof data.rulesText === 'string' && data.rulesText.length > 0
    ? data.rulesText
    : (typeof data.summary === 'string' ? data.summary : '');
  const status = data.narrationStatus;
  const llmOk = status !== 'failed'
    && status !== 'unavailable'
    && status !== 'rules'
    && data.narrationArm === 'llm'
    && typeof data.narration === 'string'
    && data.narration.length > 0;
  return llmOk ? data.narration as string : rules;
}
